import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { AuthProvider, useAuth } from './AuthContext';
import { handleBackPress, useBackClose } from './backButton';
import InstallBanner from './InstallBanner';
import LoginPage from './LoginPage';
import RoleSelector from './RoleSelector';
import AdminPanel from './AdminPanel';
import ServicePanel from './ServicePanel';
import AdminDashboard from './AdminDashboard';
import QuickEventForm from './QuickEventForm';
import ShowOnMapButton from './ShowOnMapButton';
import BlacklistManager from './BlacklistManager';
import notificationSound from './NotificationSound';
import SoundNotificationManager from './SoundNotificationManager';
import { translateText, detectLanguage } from './translationUtils';
import { LanguageProvider, useLanguage, serviceTypeName, adminDisplayName } from './i18n';
import NotificationPanel from './NotificationPanel';
import EventFeed from './EventFeed';
import ServiceDirectory from './ServiceDirectory';
import LoadingScreen from './LoadingScreen';
import MapClickHandler from './MapClickHandler';
import HeatmapLayer from './HeatmapLayer';
import './App.css';
import 'leaflet/dist/leaflet.css';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language translations
const translations = {
  kk: {
    title: "Тұрғын",
    toggleLegend: "Легенданы ауыстыру",
    emergencyMode: "ТЖ режимі",
    adminPanel: "Басқару панелі",
    dashboard: "Дашборд",
    quickAdd: "Жылдам қосу",
    loading: "Жүктеу...",
    error: "Қате орын алды",
    noEvents: "Оқиғалар табылмады",
    refreshData: "Деректерді жаңарту",
    clickToAdd: "Оқиға қосу үшін картаға басыңыз",
    exitQuickAdd: "Жылдам қосудан шығу",
    logout: "Шығу",
    myEvents: "Менің оқиғаларым",
    assignedToMe: "Маған тағайындалған",
    statusNew: "Жаңа",
    statusAssigned: "Тағайындалған",
    statusInProgress: "Орындалуда",
    statusCompleted: "Аяқталған",
    statusRejected: "Қабылданбаған",
    categories: "Санаттар",
    allEvents: "Барлық оқиғалар",
    eventsFeed: "Оқиғалар",
    notifications: "Хабарламалар",
    exitEmergency: "ТЖ-дан шығу",
    noEmergency: "Белсенді ТЖ оқиғалары жоқ",
    emergencyToggleOn: "ТЖ режимін қосу (қарапайым оқиғаларды көлеңкелеу)",
    emergencyToggleOff: "ТЖ режимін өшіру (қарапайым оқиғаларды көлеңкелеу)"
  },
  ru: {
    title: "Житель", 
    toggleLegend: "Переключить легенду",
    emergencyMode: "Режим ЧС",
    adminPanel: "Админ-панель",
    dashboard: "Дашборд",
    quickAdd: "Быстрое добавление",
    loading: "Загрузка...",
    error: "Произошла ошибка",
    noEvents: "События не найдены",
    refreshData: "Обновить данные",
    clickToAdd: "Нажмите на карту для добавления события",
    exitQuickAdd: "Выйти из режима добавления",
    logout: "Выйти",
    myEvents: "Мои события",
    assignedToMe: "Назначенные мне",
    statusNew: "Новое",
    statusAssigned: "Назначено",
    statusInProgress: "В работе",
    statusCompleted: "Выполнено",
    statusRejected: "Отклонено",
    categories: "Категории",
    allEvents: "Все события",
    eventsFeed: "События",
    notifications: "Уведомления",
    exitEmergency: "Выйти из ЧС",
    noEmergency: "Нет активных событий ЧС",
    emergencyToggleOn: "Включить режим ЧС (затенение обычных событий)",
    emergencyToggleOff: "Отключить режим ЧС (затенение обычных событий)"
  }
};

const TurgynMap = ({ isGuestMode, setIsGuestMode }) => {
  const { user, logout, isResident, isService, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const { language, setLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [legendVisible, setLegendVisible] = useState(true);
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [hasEmergencyEvents, setHasEmergencyEvents] = useState(false);
  const [emergencyModeDisabled, setEmergencyModeDisabled] = useState(false);
  const [activeCategories, setActiveCategories] = useState(new Set());
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [quickAddMode, setQuickAddMode] = useState(false);
  const [quickAddHelpVisible, setQuickAddHelpVisible] = useState(false);
  const [showQuickForm, setShowQuickForm] = useState(false);
  const [clickPosition, setClickPosition] = useState(null);
  const [tempMarker, setTempMarker] = useState(null);
  const [eventFilter, setEventFilter] = useState('all'); // all, my_events, assigned_to_me
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [showEventFeed, setShowEventFeed] = useState(false);
  const [showServicesDirectory, setShowServicesDirectory] = useState(false);
  const [selectedEventIdForFeed, setSelectedEventIdForFeed] = useState(null);
  const [eventFeedCategoryFilter, setEventFeedCategoryFilter] = useState(null);
  const [eventFeedStatusFilter, setEventFeedStatusFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [legendCollapsed, setLegendCollapsed] = useState(window.innerWidth <= 768);

  // Кнопка «Назад» на смартфоне закрывает верхний открытый экран/окно
  useBackClose(showEventFeed, () => setShowEventFeed(false));
  useBackClose(showDashboard, () => setShowDashboard(false));
  useBackClose(showAdminPanel, () => setShowAdminPanel(false));
  useBackClose(showNotifications, () => setShowNotifications(false));
  useBackClose(showServicesDirectory, () => setShowServicesDirectory(false));
  useBackClose(showQuickForm, () => {
    setShowQuickForm(false);
    setClickPosition(null);
    setTempMarker(null);
  });
  useBackClose(quickAddMode && !showQuickForm, () => setQuickAddMode(false));
  const mapRef = useRef(null);

  // Detect mobile resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile && !legendCollapsed) {
        setLegendCollapsed(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [legendCollapsed]);

  const t = translations[language];

  // Leaflet map center (Altai, Kazakhstan)
  const mapCenter = useMemo(() => [49.7144, 84.2724], []);

  // Function to get auto-translated description
  const getTranslatedDescription = useCallback((event) => {
    let description = '';
    
    if (language === 'kk') {
      description = event.description_kk || event.description_ru || '';
      if (!event.description_kk && event.description_ru) {
        description = translateText(event.description_ru, 'ru', 'kk');
      }
    } else {
      description = event.description_ru || event.description_kk || '';
      if (!event.description_ru && event.description_kk) {
        description = translateText(event.description_kk, 'kk', 'ru');
      }
    }
    
    return description;
  }, [language]);

  const loadNotificationsCount = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/notifications/count`);
      setUnreadNotificationsCount(response.data.unread_count);
    } catch (err) {
      console.error('Error loading notifications count:', err);
    }
  }, [user]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const categoriesResponse = await axios.get(`${API}/categories`);
      const categoriesData = categoriesResponse.data;
      setCategories(categoriesData);
      
      const activeCats = new Set(categoriesData.map(cat => cat.id));
      setActiveCategories(activeCats);
      
      const params = {};
      if (eventFilter === 'my_events') {
        params.created_by_me = true;
      } else if (eventFilter === 'assigned_to_me' && isService()) {
        params.assigned_to_me = true;
      }
      
      const eventsResponse = await axios.get(`${API}/events`, { params });
      setEvents(eventsResponse.data);
      
      const activeEmergencyEvents = eventsResponse.data.filter(event => 
        event.category === 'emergency' && 
        event.is_active && 
        event.status !== 'completed' && 
        event.status !== 'closed'
      );
      
      const hasActiveEmergency = activeEmergencyEvents.length > 0;
      setHasEmergencyEvents(hasActiveEmergency);
      
      if (hasActiveEmergency) {
        console.log(`🚨 Auto-enabling emergency mode: ${activeEmergencyEvents.length} active emergency events`);
        setEmergencyMode(true);
      } else {
        console.log('✅ Auto-disabling emergency mode: no active emergency events');
        setEmergencyMode(false);
      }
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(t.error);
    } finally {
      setLoading(false);
    }
  }, [eventFilter, isService, t.error]);

  // Load categories and events
  useEffect(() => {
    loadData();
    
    if (isService()) {
      loadNotificationsCount();
      const notificationInterval = setInterval(loadNotificationsCount, 30000);
      return () => clearInterval(notificationInterval);
    }
  }, [loadData, isService, loadNotificationsCount]);

  const handleEventDescriptionClick = (eventId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Clicked on event description:', eventId); // Debug log
    setSelectedEventIdForFeed(eventId);
    setShowEventFeed(true);
  };

  // Make the function globally available for popup clicks
  useEffect(() => {
    window.handleEventClick = (eventId) => {
      console.log('Global event click handler called:', eventId);
      setSelectedEventIdForFeed(eventId);
      setShowEventFeed(true);
    };
    
    return () => {
      delete window.handleEventClick;
    };
  }, []);

  // Add click handlers to popup descriptions after they're created
  useEffect(() => {
    const addPopupClickHandlers = () => {
      const descriptions = document.querySelectorAll('.clickable-description');
      descriptions.forEach(desc => {
        if (!desc.dataset.handlerAdded) {
          desc.dataset.handlerAdded = 'true';
          desc.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const eventId = desc.dataset.eventId;
            if (eventId && window.handleEventClick) {
              console.log('Description clicked for event:', eventId);
              window.handleEventClick(eventId);
            }
          });
        }
      });
    };

    // Check for new popups every second
    const intervalId = setInterval(addPopupClickHandlers, 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const handleOpenEventFeed = () => {
    setSelectedEventIdForFeed(null);
    setShowEventFeed(true);
  };

  // Helper function to get display name for service
  const getServiceDisplayName = (assignedToId, language, assignedToUser = null, eventCategory = null) => {
    if (!assignedToId) return '';

    // Приоритет: данные пользователя службы из БД (реальный UUID → название по типу службы)
    if (assignedToUser && assignedToUser.service_type) {
      const typeName = serviceTypeName(assignedToUser.service_type, language, '');
      if (typeName) return typeName;
    }
    // Если у назначенного нет service_type (например Аким) — определяем службу по категории события
    if (eventCategory) {
      const categoryToService = {
        emergency: 'emergency_service', municipal: 'municipal_service', architecture: 'architecture_service',
        heating: 'heating_service', electricity: 'electricity_service', telecom: 'telecom_service',
        traffic_police: 'traffic_police_service', police: 'police_service', improvement: 'improvement_service',
        education: 'education_service', culture_sport: 'culture_sport_service', waste: 'waste_service',
        healthcare: 'healthcare_service', lighting: 'lighting_service'
      };
      const st = categoryToService[eventCategory];
      if (st) {
        const n = serviceTypeName(st, language);
        if (n) return n;
      }
    }
    if (assignedToUser && assignedToUser.full_name) {
      return assignedToUser.full_name;
    }
    
    // Map service IDs to display names
    const serviceNames = {
      'emergency_1': {
        'ru': 'Служба ЧС',
        'kk': 'ТЖ қызметі'
      },
      'municipal_1': {
        'ru': 'Отдел ЖКХ',
        'kk': 'ТКШ бөлімі'
      },
      'architecture_1': {
        'ru': 'Отдел Архитектуры',
        'kk': 'Сәулет бөлімі'
      },
      'heating_1': {
        'ru': 'Теплоцентраль',
        'kk': 'Жылу орталығы'
      },
      'electricity_1': {
        'ru': 'ОЭСК',
        'kk': 'ОЭСК'
      },
      'telecom_1': {
        'ru': 'Казахтелеком',
        'kk': 'Қазақтелеком'
      },
      'traffic_police_1': {
        'ru': 'ГАИ',
        'kk': 'ЖАИ'
      },
      'police_1': {
        'ru': 'Полиция',
        'kk': 'Полиция'
      },
      'improvement_1': {
        'ru': 'Служба благоустройства',
        'kk': 'Зырян Тәртіп'
      },
      'education_1': {
        'ru': 'ГОРОНО',
        'kk': 'ГОРОНО'
      },
      'culture_sport_1': {
        'ru': 'Отдел Культуры и Спорта',
        'kk': 'Мәдениет және спорт бөлімі'
      },
      'waste_1': {
        'ru': 'ШемКомХоз',
        'kk': 'ШемКомХоз'
      },
      'healthcare_1': {  
        'ru': 'Горздрав',
        'kk': 'Қалалық денсаулық сақтау'
      },
      'lighting_1': {
        'ru': 'Служба освещения',
        'kk': 'Көше жарығы қызметі'
      }
    };
    
    const serviceName = serviceNames[assignedToId];
    if (serviceName) {
      return serviceName[language] || serviceName['ru'];
    }
    
    // UUID или неизвестный ID — определяем службу по подстроке, иначе общее название (не показывать код)
    const idLower = assignedToId.toLowerCase();
    const substringMap = {
      emergency: 'emergency_service', municipal: 'municipal_service', improvement: 'improvement_service',
      architecture: 'architecture_service', heating: 'heating_service', electricity: 'electricity_service',
      telecom: 'telecom_service', traffic: 'traffic_police_service', police: 'police_service',
      education: 'education_service', culture: 'culture_sport_service', waste: 'waste_service',
      ecology: 'waste_service', healthcare: 'healthcare_service', lighting: 'lighting_service', akim: 'improvement_service'
    };
    for (const [key, serviceType] of Object.entries(substringMap)) {
      if (idLower.includes(key)) {
        const n = serviceTypeName(serviceType, language);
        if (n) return n;
      }
    }
    return language === 'kk' ? 'Қала қызметі' : 'Городская служба';
  };

  // Create custom icons for different categories with service assignment visualization
  const createCustomIcon = (category, status = 'new', assignedTo = null, event = null, shouldDim = false, isEmergency = false) => {
    const categoryData = categories.find(cat => cat.id === category);
    if (!categoryData) return new L.Icon.Default();

    // Determine icon based on status
    let statusIndicator = '';
    let iconOpacity = shouldDim ? '0.5' : '1';
    
    if (status === 'completed') {
      statusIndicator = '✓';
      iconOpacity = shouldDim ? '0.4' : '0.8';
    } else if (status === 'in_progress') {
      statusIndicator = '⏳';
    } else if (status === 'assigned') {
      statusIndicator = '👤';
    } else if (status === 'rejected') {
      statusIndicator = '❌';
      iconOpacity = shouldDim ? '0.3' : '0.6';
    }

    // Service assignment visualization - colored border
    let serviceBorderColor = 'white';
    let serviceIndicator = '';
    
    if (assignedTo && event?.assigned_to_user) {
      const serviceType = event.assigned_to_user.service_type;
      
      // Service type colors and indicators
      const serviceVisuals = {
        'emergency_service': { color: '#e74c3c', indicator: '🚨' },
        'municipal_service': { color: '#f39c12', indicator: '🔧' },
        'transport_service': { color: '#3498db', indicator: '🚌' },
        'ecology_service': { color: '#27ae60', indicator: '🌱' },
        'security_service': { color: '#9b59b6', indicator: '👮' },
        'social_service': { color: '#e67e22', indicator: '🤝' },
        'construction_service': { color: '#34495e', indicator: '🏢' }
      };
      
      const serviceVisual = serviceVisuals[serviceType];
      if (serviceVisual) {
        serviceBorderColor = serviceVisual.color;
        serviceIndicator = serviceVisual.indicator;
      }
    }

    const iconHtml = `
      <div style="
        background-color: ${categoryData.color};
        border: 3px solid ${serviceBorderColor};
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        opacity: ${iconOpacity};
        ${emergencyMode && !categoryData.is_emergency ? 'filter: grayscale(100%) brightness(60%);' : ''}
        ${shouldDim ? 'filter: grayscale(50%) brightness(70%);' : ''}
        position: relative;
        ${assignedTo ? 'box-shadow: 0 0 0 2px ' + serviceBorderColor + ', 0 2px 6px rgba(0,0,0,0.3);' : ''}
        ${isEmergency ? 'animation: pulse 1s infinite;' : ''}
      ">
        ${categoryData.icon}
        ${statusIndicator ? `<span style="position:absolute; top:-3px; right:-3px; font-size:8px; background:white; border-radius:50%; width:14px; height:14px; display:flex; align-items:center; justify-content:center; box-shadow:0 1px 2px rgba(0,0,0,0.2);">${statusIndicator}</span>` : ''}
        ${serviceIndicator ? `<span style="position:absolute; bottom:-3px; left:-3px; font-size:10px; background:${serviceBorderColor}; border-radius:50%; width:16px; height:16px; display:flex; align-items:center; justify-content:center; border:1px solid white;">${serviceIndicator}</span>` : ''}
      </div>
      <style>
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      </style>
    `;

    return L.divIcon({
      html: iconHtml,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16],
      className: 'custom-marker'
    });
  };

  // Filter events by active categories and hide completed events
  const filteredEvents = useMemo(() => {
    let filtered = events.filter(event => event.is_active);
    
    // Фильтруем по активным категориям и скрываем завершенные события
    filtered = filtered.filter(event => 
      activeCategories.has(event.category) && 
      event.status !== 'completed'
    );
    
    return filtered;
  }, [events, activeCategories]);

  // Open event feed with category filter
  const openEventFeedWithCategory = (categoryId) => {
    setSelectedEventIdForFeed(null); // Сбрасываем выбранное событие
    setEventFeedCategoryFilter(categoryId); // Устанавливаем фильтр по категории
    setShowEventFeed(true); // Открываем ленту событий
  };

  const toggleCategory = (categoryId) => {
    setCategoryFilter(prev => prev === categoryId ? null : categoryId);
  };

  const handleCategoryClick = (categoryId) => {
    // Set category filter and open event feed
    setCategoryFilter(categoryId);
    setShowEventFeed(true);
    setSelectedEventIdForFeed(null); // Reset selected event
  };

  // Toggle emergency mode with proper dimming
  const toggleEmergencyMode = () => {
    console.log('Toggling emergency mode. Current:', emergencyMode); // Debug log
    setEmergencyMode(!emergencyMode);
    
    // If turning on emergency mode, also check if we have emergency events
    if (!emergencyMode) {
      const emergencyEvents = events.filter(event => 
        event.category === 'emergency' && event.is_active && event.status !== 'completed'
      );
      console.log('Emergency events found:', emergencyEvents.length); // Debug log
      setHasEmergencyEvents(emergencyEvents.length > 0);
    }
  };

  // Check for active emergency events
  const getActiveEmergencyEventsCount = () => {
    return events.filter(event => 
      event.category === 'emergency' && 
      event.is_active && 
      event.status !== 'completed' && 
      event.status !== 'closed'
    ).length;
  };

  const activeEmergencyCount = getActiveEmergencyEventsCount();

  // Get user location
  const getUserLocation = () => {
    const kk = language === 'kk';
    if (!navigator.geolocation) {
      setLocationError(kk ? 'Браузер геолокацияны қолдамайды' : 'Геолокация не поддерживается браузером');
      return;
    }

    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        
        // Center map on user location if map is available
        if (mapRef.current) {
          mapRef.current.setView([latitude, longitude], 15);
        }
      },
      (error) => {
        let errorMessage = kk ? 'Орналасқан жерді алу қатесі' : 'Ошибка получения местоположения';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = kk ? 'Геолокацияға рұқсат берілмеген' : 'Доступ к геолокации запрещен';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = kk ? 'Орналасқан жер қолжетімсіз' : 'Местоположение недоступно';
            break;
          case error.TIMEOUT:
            errorMessage = kk ? 'Күту уақыты асып кетті' : 'Превышено время ожидания';
            break;
        }
        setLocationError(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  // Auto-load user location on mount for residents and admin
  useEffect(() => {
    if ((isResident() || isAdmin()) && !isGuestMode && navigator.geolocation && !userLocation) {
      console.log('Auto-loading geolocation for user');
      getUserLocation();
    }
  }, [user, isGuestMode]);

  // Toggle quick add mode
  const toggleQuickAddMode = () => {
    setQuickAddMode(!quickAddMode);
    setQuickAddHelpVisible(!quickAddMode); // Show help when entering quick add mode
    setTempMarker(null);
    setShowQuickForm(false);
    setClickPosition(null);
  };

  // Hide quick add help on map interactions
  const hideQuickAddHelp = () => {
    setQuickAddHelpVisible(false);
  };

  // Removed handleFirstMapClick - using unified handleMapClick for all roles

  // Auto-hide quick add help after 3 seconds
  useEffect(() => {
    if (quickAddHelpVisible && quickAddMode) {
      const timer = setTimeout(() => {
        setQuickAddHelpVisible(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [quickAddHelpVisible, quickAddMode]);

  // Handle map click for quick add
  const handleMapClick = (latlng) => {
    console.log('Map clicked. Quick add mode:', quickAddMode, 'latlng:', latlng);
    
    if (quickAddMode) {
      // Hide help message if visible
      if (quickAddHelpVisible) {
        setQuickAddHelpVisible(false);
      }
      
      console.log('Processing quick add at:', latlng);
      setClickPosition(latlng);
      setTempMarker(latlng);
      setShowQuickForm(true);
    } else {
      console.log('Quick add mode is OFF');
    }
  };

  // Handle event creation from quick form
  const handleQuickEventCreated = (newEvent) => {
    setEvents(prev => [newEvent, ...prev]);
    setTempMarker(null);
    setShowQuickForm(false);
    setQuickAddMode(false);
  };

  // Close quick form
  const handleQuickFormClose = () => {
    setShowQuickForm(false);
    setTempMarker(null);
    setClickPosition(null);
  };

  // Create temporary marker icon
  const createTempIcon = () => {
    return L.divIcon({
      html: `
        <div style="
          background-color: #e74c3c;
          border: 3px solid white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
          animation: pulse 1.5s infinite;
        ">
          📍
        </div>
        <style>
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
        </style>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      className: 'temp-marker'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className={`app-container ${emergencyMode ? 'emergency-mode' : ''}`}>
      {/* Sound Notification Manager */}
      {!isGuestMode && <SoundNotificationManager user={user} enabled={true} />}
      
      {/* Header */}
      <header className="app-header">
        <div className="header-left">
          <div className="logo-title-container">
            <img src="/logo.jpg" alt="Алтай Логотип" className="app-logo-img" />
            <h1 className="app-title">{t.title}</h1>
          </div>
          <div className="user-info">
            <span className="user-name">{
              user?.role === 'service' ? serviceTypeName(user?.service_type, language, user?.full_name) :
              user?.role === 'admin' ? adminDisplayName(user?.full_name, language) :
              user?.full_name
            }</span>
            <span className="user-role">
              {user?.role === 'resident' && (language === 'kk' ? 'Тұрғын' : 'Житель')}
              {user?.role === 'service' && (language === 'kk' ? 'Қызмет' : 'Служба')}
              {user?.role === 'admin' && (language === 'kk' ? 'Әкім' : 'Аким')}
            </span>
          </div>
        </div>
        
        <div className="header-controls">
          <button 
            className="refresh-btn"
            onClick={loadData}
            disabled={loading}
          >
            🔄 {t.refreshData}
          </button>

          {/* Quick add - SAME FOR ALL ROLES */}
          {user && (
            <button 
              className={`quick-add-btn ${quickAddMode ? 'active' : ''}`}
              onClick={() => {
                console.log('Quick add button clicked. Current mode:', quickAddMode);
                console.log('User role:', user.role);
                toggleQuickAddMode();
              }}
            >
              {quickAddMode ? t.exitQuickAdd : t.quickAdd}
            </button>
          )}

          {/* Event filters for service and admin */}
          {(isService() || isAdmin()) && !isGuestMode && (
            <select 
              className="event-filter"
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
            >
              <option value="all">{t.allEvents}</option>
              <option value="my_events">{t.myEvents}</option>
              {isService() && <option value="assigned_to_me">{t.assignedToMe}</option>}
            </select>
          )}
          
          {/* Events feed button */}
          <button 
            className="events-feed-btn"
            onClick={handleOpenEventFeed}
          >
            📋 {t.eventsFeed}
          </button>

          {/* Admin panel for service */}
          {isService() && !isGuestMode && (
            <button 
              className="admin-btn"
              onClick={() => setShowAdminPanel(true)}
            >
              ⚙️ {t.adminPanel}
            </button>
          )}
          
          {/* Auto-geolocation for residents - no button needed */}

          {/* Services Directory removed - now in category dropdown */}

          {/* Notifications for service */}
          {isService() && !isGuestMode && (
            <button 
              className={`notifications-btn ${unreadNotificationsCount > 0 ? 'has-unread' : ''}`}
              onClick={() => setShowNotifications(true)}
            >
              🔔 {t.notifications}
              {unreadNotificationsCount > 0 && (
                <span className="notification-badge">{unreadNotificationsCount}</span>
              )}
            </button>
          )}
          
          {/* Dashboard for admin */}
          {isAdmin() && !isGuestMode && (
            <button 
              className="dashboard-btn"
              onClick={() => setShowDashboard(true)}
            >
              📊 {t.dashboard}
            </button>
          )}
          
          <button 
            className={`emergency-btn ${emergencyMode ? 'active' : ''} ${activeEmergencyCount > 0 ? 'has-emergency' : ''}`}
            onClick={toggleEmergencyMode}
            disabled={activeEmergencyCount === 0}
            title={activeEmergencyCount === 0 ? t.noEmergency : (emergencyMode ? t.emergencyToggleOff : t.emergencyToggleOn)}
          >
            🚨 {emergencyMode ? t.exitEmergency : t.emergencyMode}
            {activeEmergencyCount > 0 && (
              <span className="emergency-badge">{activeEmergencyCount}</span>
            )}
          </button>
          
          <select 
            className="language-switcher"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="kk">Қазақша</option>
            <option value="ru">Русский</option>
          </select>
          
          <button 
            className="logout-btn"
            onClick={() => {
              logout();
              setIsGuestMode(false);
            }}
          >
            🚪 {t.logout}
          </button>
        </div>
      </header>

      {/* Map Container */}
      <div className={`map-container ${quickAddMode ? 'quick-add-active' : ''}`}>
        
        {/* Heatmap toggle button */}
        <button
          className={`heatmap-toggle-btn ${showHeatmap ? 'active' : ''}`}
          onClick={() => setShowHeatmap(prev => !prev)}
          title={showHeatmap
            ? (language === 'kk' ? 'Жылу картасын жасыру' : 'Скрыть тепловую карту')
            : (language === 'kk' ? 'Жылу картасын көрсету' : 'Показать тепловую карту')}
          data-testid="toggle-heatmap-btn"
        >
          <span className="heatmap-icon">🔥</span>
          <span className="heatmap-label">
            {showHeatmap
              ? (language === 'kk' ? 'Карта қосулы' : 'Карта вкл.')
              : (language === 'kk' ? 'Жылу картасы' : 'Тепло карта')}
          </span>
        </button>

        <MapContainer
          center={mapCenter}
          zoom={14}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          ref={mapRef}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {/* Heatmap overlay (тепловая карта жалоб) */}
          <HeatmapLayer events={filteredEvents} visible={showHeatmap} />

          {/* Map Click Handler */}
          <MapClickHandler 
            onMapClick={handleMapClick}
            onZoom={hideQuickAddHelp}
            onMove={hideQuickAddHelp}
          />

          {/* Quick Add Help Message */}
          {quickAddHelpVisible && quickAddMode && (
            <div className="quick-add-help-message">
              {language === 'kk' ? 'Оқиға қосу үшін картаны басыңыз' : 'Нажмите на карту для добавления события'}
            </div>
          )}
          
          {/* User location marker */}
          {userLocation && (
            <Marker
              position={[userLocation.lat, userLocation.lng]}
              icon={L.divIcon({
                html: '<div class="user-location-marker"></div>',
                className: 'user-location-icon',
                iconSize: [30, 30],
                iconAnchor: [15, 15]
              })}
            >
              <Popup>
                <div className="user-location-popup">
                  <h4>{language === 'kk' ? 'Сіздің орналасқан жеріңіз' : 'Ваше местоположение'}</h4>
                  <p>Широта: {userLocation.lat.toFixed(6)}</p>
                  <p>Долгота: {userLocation.lng.toFixed(6)}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Event Markers */}
          {filteredEvents.map((event) => {
            const categoryData = categories.find(cat => cat.id === event.category);
            const isEmergency = event.category === 'emergency';
            const shouldDim = emergencyMode && !isEmergency;
            
            return (
              <Marker
                key={event.id}
                position={[event.location.lat, event.location.lng]}
                opacity={shouldDim ? 0.3 : 1.0} // Затенение неэкстренных событий
                icon={createCustomIcon(event.category, event.status, event.assigned_to, event, shouldDim, isEmergency)}
              >
              <Popup>
                <div className={`popup-content ${shouldDim ? 'dimmed-popup' : ''}`}>
                  <div className="popup-category">
                    {categories.find(cat => cat.id === event.category)?.[`name_${language}`] || event.category}
                  </div>
                  <div className="popup-status">
                    Статус: {event.status === 'new' && t.statusNew}
                    {event.status === 'assigned' && t.statusAssigned}
                    {event.status === 'in_progress' && t.statusInProgress}
                    {event.status === 'completed' && t.statusCompleted}
                    {event.status === 'rejected' && t.statusRejected}
                  </div>
                  {/* Show assigned service information */}
                  {event.assigned_to && event.status !== 'new' && (
                    <div className="popup-assigned-service">
                      <strong>
                        {language === 'kk' ? 'Жауапты қызмет: ' : 'Ответственная служба: '}
                      </strong>
                      <span className="service-name">
                        {getServiceDisplayName(event.assigned_to, language, event.assigned_to_user, event.category)}
                      </span>
                    </div>
                  )}
                  <div 
                    className="popup-description clickable-description"
                    data-event-id={event.id}
                    title={language === 'kk' ? 'Толық ақпарат үшін басыңыз' : 'Нажмите для подробной информации'}
                    style={{ 
                      cursor: 'pointer', 
                      userSelect: 'none',
                      background: 'rgba(52, 152, 219, 0.05)',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid transparent',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {event[`description_${language}`] || getTranslatedDescription(event)}
                  </div>
                  {event.photos && event.photos.length > 0 && (
                    <div className="popup-photos">
                      {event.photos.map((photo, index) => (
                        <img 
                          key={`popup-photo-${event.id}-${index}`}
                          src={`${BACKEND_URL}${photo}`}
                          alt={`Фото ${index + 1}`}
                          className="popup-photo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {event.address && (
                    <div className="popup-address">
                      📍 {event.address}
                    </div>
                  )}
                  {event.priority && (
                    <div className="popup-priority">
                      {language === 'kk' ? 'Маңыздылық' : 'Приоритет'}: {event.priority}/5
                    </div>
                  )}
                  {event.created_by_user && (
                    <div className="popup-creator">
                      {language === 'kk' ? 'Жасаған: Тұрғын' : 'Создал: Житель'} {event.created_by_user.full_name ? event.created_by_user.full_name.split(' ')[0] : ''}
                    </div>
                  )}
                  <div className="popup-time">
                    {new Date(event.created_at).toLocaleString(language === 'kk' ? 'kk-KZ' : 'ru-RU')}
                  </div>
                  <div className="popup-hint">
                    💡 {language === 'kk' ? 'Толық ақпарат үшін сипаттамаға басыңыз' : 'Нажмите на описание для подробностей'}
                  </div>
                </div>
              </Popup>
            </Marker>
            );
          })}

          {/* Temporary marker for quick add */}
          {tempMarker && (
            <Marker
              position={[tempMarker.lat, tempMarker.lng]}
              icon={createTempIcon()}
            />
          )}
        </MapContainer>

        {/* Mobile Legend Toggle Button */}
        {isMobile && (
          <button 
            className="toggle-legend-btn"
            onClick={() => setLegendCollapsed(!legendCollapsed)}
            aria-label={legendCollapsed
              ? (language === 'kk' ? 'Санаттарды көрсету' : 'Показать категории')
              : (language === 'kk' ? 'Санаттарды жасыру' : 'Скрыть категории')}
          >
            {legendCollapsed ? '📋' : '✕'}
          </button>
        )}

        {/* Legend Toggle Button (Desktop) */}
        {!isMobile && (
          <button 
            className="legend-toggle-btn"
            onClick={() => setLegendVisible(!legendVisible)}
          >
            ☰
          </button>
        )}

        {/* Legend with Categories */}
        <div className={`legend ${legendVisible ? 'visible' : 'hidden'} ${isMobile ? 'legend-panel' : ''} ${legendCollapsed && isMobile ? 'collapsed' : ''}`}>
          {/* Legend Header - clickable on mobile */}
          <div 
            className="legend-header"
            onClick={() => isMobile && setLegendCollapsed(!legendCollapsed)}
            style={{ cursor: isMobile ? 'pointer' : 'default' }}
          >
            <h3>{language === 'kk' ? '📋 Оқиғалар санаттары' : '📋 Категории событий'}</h3>
            {isMobile && (
              <button 
                className="legend-close-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setLegendCollapsed(true);
                }}
              >
                {legendCollapsed ? '▲' : '✕'}
              </button>
            )}
          </div>
          
          {/* Main Categories */}
          <div className={`legend-items ${isMobile ? 'legend-categories' : ''}`}>
            <div className="legend-section-title">
              {language === 'kk' ? '🏛️ Негізгі санаттар' : '🏛️ Основные категории'}
            </div>
            {categories.filter(category => !category.id.includes('internal_')).map((category) => (
              <div 
                key={category.id} 
                className={`legend-item clickable-category main-category ${isMobile ? 'category-item' : ''}`}
                style={{
                  opacity: emergencyMode && !category.is_emergency ? 0.3 : 1,
                  pointerEvents: emergencyMode && !category.is_emergency ? 'none' : 'auto'
                }}
                onClick={() => {
                  handleCategoryClick(category.id);
                  if (isMobile) setLegendCollapsed(true);
                }}
                title={language === 'kk' ? 'Осы санаттағы оқиғаларды көру үшін басыңыз' : 'Нажмите для просмотра событий этой категории'}
              >
                <span 
                  className={`legend-icon ${isMobile ? 'category-icon' : ''}`}
                  style={{ 
                    filter: emergencyMode && !category.is_emergency ? 'grayscale(100%) brightness(60%)' : 'none'
                  }}
                >
                  {category.icon || '📋'}
                </span>
                <span className="legend-text">
                  {category[`name_${language}`]}
                </span>
                <span className="legend-count">
                  ({events.filter(e => e.category === category.id && e.is_active && e.status !== 'completed').length})
                </span>
              </div>
            ))}
            
          </div>
        </div>

        {/* Logo */}
        <div className="app-logo">
          {t.title}
        </div>
      </div>

      {/* Service Panel for Service */}
      {showAdminPanel && isService() && (
        <ServicePanel onClose={() => {
          setShowAdminPanel(false);
          loadData(); // Refresh data when service panel closes
        }} />
      )}

      {/* Dashboard for Admin */}
      {showDashboard && isAdmin() && (
        <AdminDashboard 
          onClose={() => {
            setShowDashboard(false);
            loadData(); // Refresh data when dashboard closes
          }}
          onOpenEventFeed={(categoryId, statusFilter) => {
            if (categoryId) {
              setCategoryFilter(categoryId);
              setEventFeedStatusFilter(null);
            } else if (statusFilter) {
              setEventFeedStatusFilter(statusFilter);
              setCategoryFilter(null);
            }
            setShowEventFeed(true);
            setSelectedEventIdForFeed(null);
          }}
          onSelectEvent={(eventId) => {
            setSelectedEventIdForFeed(eventId);
            setShowEventFeed(true);
            setCategoryFilter(null);
            setEventFeedStatusFilter(null);
          }}
        />
      )}

      {/* Notification Panel for Service */}
      {showNotifications && isService() && (
        <NotificationPanel 
          onClose={() => {
            setShowNotifications(false);
            loadNotificationsCount(); // Refresh count when panel closes
          }}
          onEventSelect={(eventId) => {
            setSelectedEventIdForFeed(eventId);
            setShowEventFeed(true);
          }}
        />
      )}

      {/* Quick Event Form */}
      {showQuickForm && clickPosition && (isResident() || isService() || isAdmin()) && (
        <QuickEventForm
          position={clickPosition}
          categories={categories}
          language={language}
          user={user}
          onClose={handleQuickFormClose}
          onEventCreated={handleQuickEventCreated}
        />
      )}
      
      {/* Service Directory */}
      {showServicesDirectory && (
        <ServiceDirectory 
          onClose={() => setShowServicesDirectory(false)}
          language={language}
        />
      )}

      {/* Event Feed */}
      {showEventFeed && (
        <EventFeed
          onClose={() => {
            setShowEventFeed(false);
            setSelectedEventIdForFeed(null);
            setCategoryFilter(null);
            setEventFeedCategoryFilter(null);
            setEventFeedStatusFilter(null);
          }}
          selectedEventId={selectedEventIdForFeed}
          categoryFilter={categoryFilter || eventFeedCategoryFilter}
          statusFilter={eventFeedStatusFilter}
          language={language}
          onShowOnMap={(event) => {
            // Центрируем карту на событии
            if (mapRef.current && event.location) {
              mapRef.current.setView([event.location.lat, event.location.lng], 16);
              setShowEventFeed(false); // Закрываем ленту событий
            }
          }}
        />
      )}
    </div>
  );
};

// Main App component with AuthProvider
function App() {
  const [showLoadingScreen, setShowLoadingScreen] = useState(() => {
    // Не показываем заставку, если есть признак активной сессии
    // (сам токен хранится в httpOnly cookie и недоступен из JS)
    return !localStorage.getItem('has_session');
  });  const [showDemoSelector, setShowDemoSelector] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [appClosed, setAppClosed] = useState(false);
  const exitDialogRef = useRef(false);
  const allowExitRef = useRef(false);

  // Перехват аппаратной кнопки «Назад» (Android / браузер)
  useEffect(() => {
    window.history.pushState({ turgyn: true }, '');

    const onPopState = () => {
      if (allowExitRef.current) return; // пользователь подтвердил выход
      window.history.pushState({ turgyn: true }, '');

      if (exitDialogRef.current) {
        // «Назад» при открытом диалоге выхода = остаться
        exitDialogRef.current = false;
        setShowExitDialog(false);
        return;
      }

      // Закрываем верхний открытый экран/окно; если ничего не открыто — диалог выхода
      if (!handleBackPress()) {
        exitDialogRef.current = true;
        setShowExitDialog(true);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const handleExitConfirm = () => {
    allowExitRef.current = true;
    exitDialogRef.current = false;
    setShowExitDialog(false);

    // 1) Снимаем нашу sentinel-запись истории: после этого аппаратная «Назад»
    // снова работает нативно (сворачивает/закрывает приложение на Android)
    try { window.history.back(); } catch (e) { /* noop */ }

    // 2) Пытаемся закрыть окно (работает в установленном PWA / окне из window.open)
    setTimeout(() => {
      try { window.close(); } catch (e) { /* noop */ }
    }, 150);

    // 3) Если окно не закрылось — показываем экран «Приложение закрыто».
    // История уже раскручена, поэтому следующая аппаратная «Назад» реально
    // сворачивает/закрывает приложение (нативное поведение Android).
    setTimeout(() => {
      setAppClosed(true);
    }, 450);
  };

  const handleExitCancel = () => {
    exitDialogRef.current = false;
    setShowExitDialog(false);
  };

  // Handle guest login
  const handleGuestLogin = () => {
    setIsGuestMode(true);
    setShowLoadingScreen(false);
  };

  // Handle demo login
  const handleDemoLogin = () => {
    setShowDemoSelector(true);
  };

  const handleLoadingFinish = () => {
    setShowLoadingScreen(false);
  };

  const exitDialog = showExitDialog && (
    <div className="exit-dialog-overlay" data-testid="exit-dialog">
      <div className="exit-dialog">
        <img src="/logo.jpg" alt="Тұрғын" className="exit-dialog-logo" />
        <h2 className="exit-dialog-title">Тұрғын</h2>
        <p className="exit-dialog-text">
          Қосымшадан шығу керек пе?<br />
          Выйти из приложения?
        </p>
        <div className="exit-dialog-buttons">
          <button
            className="exit-dialog-btn exit-dialog-btn-stay"
            onClick={handleExitCancel}
            data-testid="exit-dialog-stay-btn"
          >
            Қалу / Остаться
          </button>
          <button
            className="exit-dialog-btn exit-dialog-btn-exit"
            onClick={handleExitConfirm}
            data-testid="exit-dialog-exit-btn"
          >
            Шығу / Выйти
          </button>
        </div>
      </div>
    </div>
  );

  if (appClosed) {
    return (
      <div
        data-testid="app-closed-screen"
        style={{
          margin: 0, minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', background: '#0b1220',
          color: '#cbd5e1', textAlign: 'center', padding: 24,
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
        }}
      >
        <img src="/logo.jpg" alt="Тұрғын" style={{ width: 64, height: 64, borderRadius: 16, marginBottom: 16, opacity: 0.9 }} />
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Тұрғын</div>
        <div style={{ fontSize: 14, opacity: 0.8 }}>Қосымша жабылды<br />Приложение закрыто</div>
        <div style={{ fontSize: 12, opacity: 0.5, marginTop: 16 }}>
          «Артқа» батырмасын басыңыз немесе бетті жабыңыз<br />
          Нажмите «Назад» ещё раз или закройте эту вкладку
        </div>
        <button
          data-testid="app-closed-reopen-btn"
          onClick={() => window.location.replace('/')}
          style={{
            marginTop: 24, padding: '10px 24px', borderRadius: 24, border: '1px solid #334155',
            background: 'transparent', color: '#94a3b8', fontSize: 13, cursor: 'pointer'
          }}
        >
          Қайта ашу / Открыть снова
        </button>
      </div>
    );
  }

  if (showLoadingScreen) {
    return (
      <>
        <LoadingScreen onFinish={handleLoadingFinish} />
        {exitDialog}
        <InstallBanner />
      </>
    );
  }

  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent 
          showDemoSelector={showDemoSelector}
          setShowDemoSelector={setShowDemoSelector}
          isGuestMode={isGuestMode}
          setIsGuestMode={setIsGuestMode}
          handleDemoLogin={handleDemoLogin}
          handleGuestLogin={handleGuestLogin}
        />
        {exitDialog}
        <InstallBanner />
      </AuthProvider>
    </LanguageProvider>
  );
}

// App content that uses auth context
const AppContent = ({ 
  showDemoSelector, 
  setShowDemoSelector, 
  isGuestMode, 
  setIsGuestMode,
  handleDemoLogin, 
  handleGuestLogin 
}) => {
  const { user, loading } = useAuth();
  const { language, setLanguage } = useLanguage();

  // Кнопка «Назад»: выбор роли -> стартовая страница; гостевой режим -> стартовая страница
  useBackClose(showDemoSelector && !user && !isGuestMode, () => setShowDemoSelector(false));
  useBackClose(isGuestMode && !user, () => setIsGuestMode(false));

  // Reset guest mode when user logs in successfully
  useEffect(() => {
    if (user && isGuestMode) {
      console.log('User logged in, resetting guest mode');
      setIsGuestMode(false);
    }
  }, [user, isGuestMode, setIsGuestMode]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{language === 'kk' ? 'Жүктелуде...' : 'Загрузка...'}</p>
      </div>
    );
  }

  // Show demo selector if activated
  if (showDemoSelector && !user && !isGuestMode) {
    return (
      <RoleSelector 
        onBackToAuth={() => setShowDemoSelector(false)}
        language={language}
      />
    );
  }

  // Show login page if no user and not guest mode
  if (!user && !isGuestMode) {
    return (
      <LoginPage 
        onDemoLogin={handleDemoLogin}
        onGuestLogin={handleGuestLogin}
        language={language}
        setLanguage={setLanguage}
      />
    );
  }

  return <TurgynMap isGuestMode={isGuestMode} setIsGuestMode={setIsGuestMode} />;
};

export default App;